import type { PropsWithChildren } from 'react';
import type React from 'react';
import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { useBreakpoints } from '@xing-com/crate-jobs-hooks';

import { FormField } from './form-fields.common.styles';
import { useTrackFieldFocus } from './hooks';
import type { FormFieldPropsWithCharacterLimit } from './types';

const ONE_EXTRA_CHAR_LEFT = -1;

const StyledFormField = styled(FormField)`
  p:last-of-type {
    text-align: right;
  }
`;

export const CommentsField = <T extends FieldValues>({
  control,
  path,
  characterLimit,
}: PropsWithChildren<
  FormFieldPropsWithCharacterLimit<T>
>): React.ReactElement => {
  const intl = useIntl();
  const { isMediaSmallOrTiny } = useBreakpoints();
  const onCommentsFocus = useTrackFieldFocus({
    isAutofilled: false,
    isMandatory: false,
    fieldName: 'comments',
  });

  return (
    <Controller
      name={path}
      control={control}
      render={({ field: { ref, ...fieldProps } }) => {
        const charsLeft = characterLimit - fieldProps.value.length;
        return (
          <StyledFormField
            variant="plain"
            multiline
            size="large"
            // @ts-expect-error FIXME: SC6
            minRows={isMediaSmallOrTiny ? 7 : 4}
            {...fieldProps}
            aria-label={intl.formatMessage({
              id: 'JOBS_INSTANT_APPLY_FORM_ADDITIONAL_COMMENTS_HEADLINE',
            })}
            innerRef={ref}
            error={charsLeft < 0}
            helperText={
              charsLeft < 0
                ? intl.formatMessage(
                    {
                      id:
                        charsLeft === ONE_EXTRA_CHAR_LEFT
                          ? 'JOBS_INSTANT_APPLY_FORM_ADDITIONAL_COMMENTS_FIELD_ERROR_ONE'
                          : 'JOBS_INSTANT_APPLY_FORM_ADDITIONAL_COMMENTS_FIELD_ERROR',
                    },
                    {
                      number: Math.abs(charsLeft),
                    }
                  )
                : `${intl.formatMessage(
                    {
                      id: 'JOBS_INSTANT_APPLY_FORM_ADDITIONAL_COMMENTS_FIELD_HELPER',
                    },
                    {
                      number: charsLeft,
                    }
                  )}`
            }
            onFocus={onCommentsFocus}
          />
        );
      }}
      rules={{
        maxLength: characterLimit,
      }}
    />
  );
};
